import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Users,
  TrendingUp,
  Target,
  Shield,
  CheckCircle,
  ArrowRight,
  Briefcase,
  Compass
} from 'lucide-react'
import SEO from '@components/SEO'
import { useContent } from '@context/ContentContext'
import Reveal from '@components/dimensional/Reveal'
import TiltCard from '@components/dimensional/TiltCard'
import BrandPrimitives from '@components/dimensional/BrandPrimitives'
import RewardsArchitecture from '@components/dimensional/RewardsArchitecture'

const AboutPage = () => {
  const { content, loading } = useContent()

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  // Stats removed - focus on value proposition for new company

  // Where the practice actually spends its time. No invented numbers.
  const practice = [
    {
      label: 'Design work',
      value: 'Grades, ranges, differentials'
    },
    {
      label: 'Incentives',
      value: 'Equity, LTI, variable pay'
    },
    {
      label: 'Governance',
      value: 'Board and NRC papers'
    }
  ]

  const values = [
    {
      no: '01',
      icon: Target,
      title: 'Purpose before pay',
      description: 'We start with what the business is for and which roles actually move it. Pay comes last, because it is the consequence of that reasoning — not the starting point. Run the order backwards and you end up with the market median wearing your logo.'
    },
    {
      no: '02',
      icon: Shield,
      title: 'First principles, not inherited defaults',
      description: 'We take a structure apart — grade count, range width, midpoint progression, the differentials nobody can explain — and ask what each piece is doing. Most of it is inherited. Some of it is load-bearing. Knowing which is which is the job.'
    },
    {
      no: '03',
      icon: Users,
      title: 'Differentiate on contribution',
      description: 'Not every role creates the same value, and pretending otherwise is expensive. We separate roles by contribution, scarcity and risk, then write down the reasoning so it survives the next comp cycle and the next CHRO.'
    },
    {
      no: '04',
      icon: TrendingUp,
      title: 'Structures that keep working',
      description: 'Ranges age. Markets move. We build the refresh mechanics into the design — trigger points, review cadence, a correction budget — so the structure holds without an annual rescue project.'
    }
  ]

  const team = [
    {
      name: 'Manit Mittal',
      title: 'Chief Executive Officer',
      expertise: 'Executive Compensation & Strategic Advisory',
      experience: '15+ years',
      education: 'People → Purpose → Pay framework',
      image: '/manit_trp.jpeg',
      bio: 'Manit built the People → Purpose → Pay framework the firm runs on. His work sits where compensation meets governance: executive pay design, peer group construction, board and NRC papers, and the awkward conversations that come before a structure changes.'
    },

    {
      name: 'Karan Bindal',
      title: 'Chief Technology Officer',
      expertise: 'Technology & Compensation Analytics',
      experience: '10+ years',
      education: 'Analytics, modelling and automation',
      image: '/karan_trp.jpeg',
      bio: 'Karan builds the modelling and analytics layer — dilution models, range-penetration analysis, merit matrices that recalculate instead of being retyped. You keep the workbooks. The design should outlive the engagement that produced it.'
    }
  ]

  // Achievements removed - focus on current capabilities and value proposition

  const differentiators = [
    'Boutique by choice — the people you meet are the people doing the work',
    'Equity, executive pay, variable pay and benefits handled under one roof',
    'India-market fluency, tested against global reward frameworks',
    'Board-ready documentation rather than a deck of directional slides',
    'You keep the models. We are not building a dependency'
  ]

  // Get page content from Firestore
  const pageContent = content.pages?.about || {}
  const heroTitle = pageContent.hero?.title || 'Redefining Total Rewards Excellence'
  const heroSubtitle = pageContent.hero?.subtitle || 'We are Total Rewards Pro – a boutique consulting firm dedicated to transforming how organizations design, implement, and optimize their compensation and benefits strategies.'

  if (loading) {
    return (
      <div className="pt-14 min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/10 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-14">
      <SEO
        title="About Total Rewards Pro - Comprehensive HR Services Experts"
        description="Meet the team behind our comprehensive HR services framework. Manit Mittal (CEO) and Karan Bindal (CTO) lead Total Rewards Pro's mission to deliver complete HR solutions across 10 key domains using our MECE methodology."
        keywords="Total Rewards Pro team, comprehensive HR services experts, Manit Mittal CEO, Karan Bindal CTO, HR consulting experts, MECE framework, total rewards consulting, performance appraisals, executive compensation"
      />

      {/* Hero Section */}
      <section className="pt-16 lg:pt-24 pb-20 lg:pb-24 text-white relative overflow-hidden grain">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/about-hero.webp" type="image/webp" />
          <img
            src="/about-hero.jpg"
            alt="A leather card wallet holding payment and benefit cards"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Depth stack: base darkening, drifting gold mesh, then a vignette
            that pushes the frame edges back and holds the eye on the headline. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/75 to-black/95 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 mesh-drift opacity-70 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(118% 80% at 50% 8%, transparent 24%, rgba(0,0,0,0.55) 62%, rgba(0,0,0,0.92) 100%)'
          }}
        />

        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <motion.div
            className="text-center max-w-page mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div
              className="inline-flex items-center gap-3 surface-raised-gold edge-lit rounded-pill px-5 py-2.5 mb-9"
              variants={fadeInUp}
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_10px_rgba(255,209,24,0.9)]"
                aria-hidden="true"
              ></span>
              <span className="text-mono-label text-primary-300">
                About the firm
              </span>
            </motion.div>

            <motion.h1
              className="text-display-xl text-white mb-8 text-balance"
              variants={fadeInUp}
            >
              {heroTitle}
            </motion.h1>

            <motion.p
              className="text-body-large text-neutral-300 mb-10 max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={fadeInUp}
            >
              <Link to="/contact" className="btn-accent sheen px-8 py-3.5">
                Work with us
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
              >
                What we do
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hairline" aria-hidden="true"></div>
      </section>

      {/* Practice rail — pressed into the page rather than floating on it */}
      <section className="bg-black relative overflow-hidden py-10 lg:py-14" aria-label="Where the practice works">
        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="max-w-page mx-auto surface-inset rounded-card border-x border-b border-white/5 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
            {practice.map((item) => (
              <div key={item.label} className="px-8 py-8 text-center sm:text-left">
                <div className="text-mono-label text-primary-500 mb-3">
                  {item.label}
                </div>
                <div className="text-lg lg:text-xl text-neutral-200 font-open-sans tracking-tight">
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="py-20 lg:py-24 gradient-premium relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto w-full px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16 items-center">
            <Reveal className="order-2 lg:order-1">
              <div className="w-full max-w-[360px] sm:max-w-[400px] mx-auto aspect-square">
                <BrandPrimitives
                  variant="cluster"
                  className="w-full h-full"
                  label="Three metallic forms — cube, cone and sphere — held in one composition, standing for the parts of a reward structure"
                />
              </div>
              <p className="text-body-small text-neutral-400 mt-6 max-w-sm mx-auto text-center lg:text-left">
                Three parts, one object. Base, incentives and equity are usually decided in separate
                meetings — which is exactly why they end up saying different things.
              </p>
            </Reveal>

            <Reveal delay={110} className="order-1 lg:order-2">
              <div className="surface-raised-gold edge-lit rounded-card-lg p-10 lg:p-14 text-center">
                <blockquote className="text-2xl lg:text-4xl font-semibold mb-6 tracking-tight text-balance font-open-sans">
                  <span className="text-gold-metal">
                    &ldquo;Benchmarking breeds sameness. Strategy demands difference.&rdquo;
                  </span>
                </blockquote>
                <div className="hairline max-w-xs mx-auto mb-6" aria-hidden="true"></div>
                <p className="text-body-large text-neutral-300 max-w-2xl mx-auto text-balance">
                  We started Total Rewards Pro to move companies off market anchoring and onto mission
                  anchoring. Pay is a signal. Design it deliberately and it tells people where to spend
                  their week.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 lg:py-24 bg-black relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <Reveal>
              <p className="text-mono-label text-primary-500 mb-5">
                <span className="text-neutral-600">01 / 03</span>
                <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                Our philosophy
              </p>
              <h2 className="text-section-title text-white mb-8 text-balance">Beyond the benchmark</h2>
              <div className="space-y-6 text-body text-neutral-300">
                <p>
                  <strong className="text-white">Most pay structures are inherited, not designed.</strong>{' '}
                  A survey cut becomes a range. The range gets refreshed each year. Nobody goes back
                  and asks why the grades sit where they sit, or what the differential between them
                  was ever meant to signal.
                </p>
                <p>
                  People &rarr; Purpose &rarr; Pay is how we work around that. Establish which roles
                  create value and why the business exists to do it. Then let grades, ranges,
                  incentives and equity fall out of that order rather than out of a percentile.
                </p>
                <p>
                  It suits companies that have outgrown the median. Scale-ups pricing a first option
                  pool. Listed firms defending a pay ratio to a board. Businesses whose best people
                  are being quoted numbers the market average cannot explain.
                </p>
              </div>

              <div className="hairline my-10" aria-hidden="true"></div>

              <div className="surface-inset rounded-card p-8">
                <h3 className="text-heading-2 text-white mb-4">Our mission</h3>
                <p className="text-body text-neutral-300">
                  Move organisations from market anchoring to mission anchoring — and build reward
                  systems that breathe with the business instead of freezing on the day they ship.
                </p>
              </div>
            </Reveal>

            <Reveal delay={110} className="scene">
              <div className="surface-raised edge-lit rounded-card-lg p-8 lg:p-10 depth-layer">
                <div className="flex items-start gap-4 mb-8">
                  <div
                    className="w-12 h-12 surface-raised-gold rounded-xl flex items-center justify-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <Compass className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-heading-1 text-white mb-2">How we work, in five lines</h3>
                    <p className="text-body-small text-neutral-400">
                      What an engagement with us actually commits to.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {differentiators.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 surface-inset rounded-xl px-5 py-4"
                    >
                      <CheckCircle
                        className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-body-small text-neutral-300">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="hairline my-8" aria-hidden="true"></div>

                <Link
                  to="/services"
                  className="inline-flex w-full items-center justify-center rounded-md px-6 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-primary-500/25 transition-colors duration-300 hover:border-primary-500/70 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
                >
                  See the full scope of work
                  <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* What the order produces — the stack the philosophy above builds toward. */}
      <section
        className="py-20 lg:py-24 bg-black relative overflow-hidden grain"
        aria-labelledby="stack-heading"
      >
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="surface-raised edge-lit rounded-card-lg p-6 sm:p-8 lg:p-10">
                <RewardsArchitecture className="w-full h-auto" />
              </div>
            </Reveal>

            <Reveal delay={110}>
              <p className="text-mono-label text-primary-500 mb-5">The stack</p>
              <h2 id="stack-heading" className="text-section-title text-white mb-6 text-balance">
                Four layers, designed in one conversation
              </h2>
              <div className="space-y-6 text-body text-neutral-300">
                <p>
                  Base pay, benefits, variable pay and equity are four different instruments. Each one
                  buys something the others cannot — certainty, protection, focus for a year, a stake
                  in the decade.
                </p>
                <p>
                  Designed separately, they overlap and cancel out. Designed together, the shape of the
                  stack tells a candidate what kind of company this is before anyone quotes a number.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-24 gradient-premium relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_0.65fr] gap-12 lg:gap-16 items-center mb-16">
            <Reveal className="max-w-4xl">
              <p className="text-mono-label text-primary-500 mb-5">
                <span className="text-neutral-600">02 / 03</span>
                <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
                How we work
              </p>
              <h2 className="text-display-lg text-white mb-6 text-balance">
                Four rules behind every design
              </h2>
              <p className="text-body-large text-neutral-400">
                And, just as usefully, what gets left out of one.
              </p>
            </Reveal>

            <Reveal delay={110}>
              <div className="w-full max-w-[260px] sm:max-w-[300px] mx-auto aspect-square">
                <BrandPrimitives
                  variant="column"
                  className="w-full h-full"
                  label="Three forms stacked in a vertical column, standing for the order the work runs in: people, then purpose, then pay"
                />
              </div>
              <p className="text-body-small text-neutral-500 mt-5 text-center">
                People, then purpose, then pay. The order is the method.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Reveal key={index} className="scene h-full" delay={index * 90}>
                {/* No `.sheen` here — its overflow:hidden would flatten the raise. */}
                <TiltCard className="surface-raised edge-lit rounded-card-lg h-full">
                  <div className="p-8 lg:p-10 h-full flex flex-col depth-layer">
                    <div className="flex items-start justify-between mb-7">
                      <div className="w-14 h-14 surface-raised-gold rounded-xl flex items-center justify-center tilt-raise">
                        <value.icon className="w-7 h-7 text-primary-400" aria-hidden="true" />
                      </div>
                      <span className="text-mono-label text-neutral-600" aria-hidden="true">
                        {value.no} / 04
                      </span>
                    </div>

                    <h3 className="text-heading-2 text-white mb-4 text-balance">{value.title}</h3>
                    <div className="hairline mb-5" aria-hidden="true"></div>
                    <p className="text-body text-neutral-300">{value.description}</p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 lg:py-24 bg-black relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <Reveal className="max-w-3xl mb-16">
            <p className="text-mono-label text-primary-500 mb-5">
              <span className="text-neutral-600">03 / 03</span>
              <span className="mx-3 text-neutral-700" aria-hidden="true">&mdash;</span>
              The partners
            </p>
            <h2 className="text-section-title text-white mb-6 text-balance">
              Two founders. No handover to a junior team.
            </h2>
            <p className="text-body-large text-neutral-400">
              One builds the compensation architecture. The other builds the systems that keep it
              accurate after we leave.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {team.map((member, index) => (
              <Reveal key={index} delay={index * 100}>
                <div className="surface-raised edge-lit rounded-card-lg p-8 lg:p-10 h-full">
                  <div className="flex items-center gap-5 mb-7">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 ring-1 ring-primary-500/30 shadow-[0_10px_24px_-10px_rgba(0,0,0,0.9)]">
                      <img
                        src={member.image}
                        alt={`${member.name} - ${member.title}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div>
                      <h3 className="text-heading-2 text-white mb-1">{member.name}</h3>
                      <p className="text-primary-400 font-semibold text-sm font-open-sans">
                        {member.title}
                      </p>
                      <p className="text-body-small text-neutral-400 mt-1">{member.expertise}</p>
                    </div>
                  </div>

                  <div className="hairline mb-7" aria-hidden="true"></div>

                  <p className="text-body text-neutral-300 mb-7">{member.bio}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 surface-inset rounded-pill px-5 py-3">
                      <Briefcase className="w-4 h-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
                      <span className="text-body-small text-neutral-300">{member.experience}</span>
                    </div>
                    <div className="flex items-center gap-3 surface-inset rounded-pill px-5 py-3">
                      <Target className="w-4 h-4 text-primary-500 flex-shrink-0" aria-hidden="true" />
                      <span className="text-body-small text-neutral-300">{member.education}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 gradient-premium text-white relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto container-padding relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <Reveal>
                <h2 className="text-section-title text-white mb-6 text-balance">
                  Find out what your pay structure is actually saying
                </h2>
                <p className="text-body-large text-neutral-300 mb-10 max-w-2xl mx-auto lg:mx-0 text-balance">
                  Bring the org chart, the current ranges and the problem you keep re-solving every
                  cycle. Half an hour is usually enough to tell you whether we are the right firm for it.
                </p>
              </Reveal>

              <Reveal delay={90}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link to="/contact" className="btn-accent sheen text-base px-10 py-4">
                    Book a 30-minute call
                    <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center justify-center rounded-md px-10 py-4 text-base font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
                  >
                    Explore our services
                  </Link>
                </div>
              </Reveal>
            </div>

            <Reveal delay={150}>
              <div className="w-full max-w-[320px] sm:max-w-[380px] mx-auto aspect-square">
                <BrandPrimitives
                  variant="trio"
                  className="w-full h-full"
                  label="Three metallic forms turning slowly side by side, the firm's mark for a reward structure in balance"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AboutPage
